import { VisibilityToggleIcon, DeleteButton } from "../../../../components/shared";
import { useUpdateProjectFile } from "../../../../hooks/useUpdateProjectFile";
import { useDeleteProjectFile } from "../../../../hooks/useDeleteProjectFile";
import { ProjectFileVisibilityEnum } from "../../../../types/projectFile";

export interface DiaryEntryFileItemData {
  id: string;
  name: string;
  url?: string | null;
  size?: number | null;
  mediaType?: string | null;
  visibility?: string | null;
}

interface DiaryEntryFileItemProps {
  file: DiaryEntryFileItemData;
  projectId: string;
  canManage: boolean;
  onChange?: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DiaryEntryFileItem({
  file,
  projectId,
  canManage,
  onChange,
}: DiaryEntryFileItemProps) {
  const { updateProjectFile, loading: updateLoading } = useUpdateProjectFile(projectId);
  const { deleteProjectFile } = useDeleteProjectFile(projectId);

  const meta = [file.mediaType, file.size != null && file.size > 0 ? formatFileSize(file.size) : null]
    .filter(Boolean)
    .join(" · ");

  const handleToggleVisibility = async () => {
    const isInternal =
      file.visibility === ProjectFileVisibilityEnum.Internal || file.visibility === "internal";
    const nextVisibility = isInternal ? ProjectFileVisibilityEnum.Shared : ProjectFileVisibilityEnum.Internal;
    try {
      await updateProjectFile({ id: file.id, visibility: nextVisibility });
      onChange?.();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    await deleteProjectFile(file.id);
  };

  return (
    <li className="flex flex-wrap items-center gap-2">
      {canManage && (
        <VisibilityToggleIcon
          visibility={
            file.visibility === ProjectFileVisibilityEnum.Internal || file.visibility === "internal"
              ? "internal"
              : "shared"
          }
          onClick={handleToggleVisibility}
          disabled={updateLoading}
          iconSize="sm"
        />
      )}
      <span className="min-w-0 flex-1">
        {file.url ? (
          <a
            href={file.url}
            download={file.name}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-800 dark:text-primary-200 hover:text-primary-600 dark:hover:text-primary-400 underline text-sm"
          >
            {file.name}
          </a>
        ) : (
          <span className="text-sm text-primary-600 dark:text-primary-400">{file.name}</span>
        )}
        {meta && (
          <span className="ml-2 text-xs text-primary-500 dark:text-primary-500">({meta})</span>
        )}
      </span>
      {canManage && (
        <DeleteButton
          onDelete={handleDelete}
          onSuccess={onChange}
          ariaLabel="Excluir arquivo"
          iconSize="sm"
          title="Excluir Arquivo"
          message="Tem certeza que deseja excluir este arquivo?"
        />
      )}
    </li>
  );
}
